import { Result } from "../../../shared/api/services/result";
import { triggerDoctorInfoRefresh } from "../../../shared/utils/events/DoctorInfoRefreshEvent";
import { AuthApiService } from "../../auth/api/AuthApiService";
import { isMockEnabled, MOCK_SPECIALIZATIONS, MOCK_PRACTITIONERS, MOCK_UNIVERSITIES, MOCK_ORGANIZATIONS, MOCK_HOSPITALS } from "../../../shared/dev/mockData";
import { uploadErrorMessage, uploadErrorMessageFromText } from "../../../shared/utils/uploadErrors";
export type { DoctorVerificationResponse, GetPhoneByLicenseResponse } from "../../auth/api/AuthApiService";

// Navigation validation interfaces for three-section registration flow
export interface NavigationValidationRequest {
  target_step: string;
}

export interface NavigationValidationResponse {
  status: boolean;
  status_code: number;
  data: {
    can_navigate: boolean;
    current_step: string;
    current_section: "login_otp" | "id_face" | "profile";
    target_section: "login_otp" | "id_face" | "profile";
    error_message: string | null;
    will_reset_progress: boolean;
    reset_to_step: string | null;
  };
}

export interface RegistrationDoctor {
  id: string;
  phone_number: string;
  display_name: string | null;
  status:
    | "Active"
    | "Disabled"
    | "Suspended"
    | "Deleted"
    | "InReview"
    | "DataRequested"
    | "InCompleted"
    | "InConfirmed";
  registration_progress: RegistrationProgress | null;
  created_at: string;
}

export type RegistrationProgress =
  | "scan_id"
  | "face_live"
  | "doctor_info"
  | "doctor_documents"
  | "choose_doctor_point"
  | "clinic_info"
  | "uploading_docs"
  | "agree_terms"
  | "pay"
  | "completed"
  | "SCAN_ID"
  | "FACE_LIVE"
  | "DOCTOR_INFO"
  | "DOCTOR_DOCUMENTS"
  | "CHOOSE_DOCTOR_POINT"
  | "CLINIC_INFO"
  | "UPLOADING_DOCS"
  | "AGREE_TERMS"
  | "PAY"
  | "COMPLETED";


export interface DoctorInfo {
  id: string;
  governorate: string;
  district: string;
  sub_district: string;
  alley: string;
  house_number: string;
  dob: string; // YYYY-MM-DD
  sex: "Male" | "Female";
  medical_career: string;
  neighborhood: string;
  ministry: string; // Now required
  department?: string;
  hospital?: string;
  university?: string;
  college?: string;
}

export interface ClinicInfo {
  id: string;
  governorate: string;
  district: string;
  sub_district: string;
  alley: string;
  house: string;
  nearest_landmark?: string;
  latitude: number;
  longitude: number;
  phone_number: string;
  working_hours: string;
  tax_number?: string;
  has_other_clinic: boolean;
  supervises_medical_center: boolean;
  neighborhood: string;
}

/** Fees charged per request type, served from the backend's PAYMENT_*_FEE settings. */
export interface PaymentFees {
  currency: string;
  registration: number;
  renewal: number;
  relocation: number;
  closure: number;
}

export interface SubscriptionInfo {
  id: string;
  plan_name: string;
  status: string;
  start_date: string;
  end_date: string;
  amount: number;
  currency: string;
}


// Face Live interfaces
export interface PresignedUrlData {
  upload_url: string;
  fields: Record<string, string>;
  file_key: string;
  expires_at: string;
}

export interface GeneratePresignedUrlsRequest {
  file_count?: number;
  file_type?: string;
  max_file_size?: number;
}

export interface GeneratePresignedUrlsResponse {
  status: boolean;
  status_code: number;
  data: PresignedUrlData[];
}

export interface FaceLiveImageInfo {
  file_key: string;
  file_name: string;
  file_size: number;
  content_type: string;
  uploaded_at?: string;
}

export interface SaveFaceLiveImagesRequest {
  images: FaceLiveImageInfo[];
}

export interface SaveFaceLiveImagesResponse {
  status: boolean;
  status_code: number;
  data: {
    saved_count: number;
    registration_progress: string;
    message: string;
  };
}

// Choose Doctor Procedure interfaces
export interface ChooseDoctorProcedureRequest {
  doctor_procedure:
    | "OPEN_CLINIC"
    | "REGISTER_EXISTING_CLINIC"
    | "TRANSFER_CLINIC"
    | "RELOCATE_CLINIC"
    | "CLOSE_CLINIC"
    | "RENEW_LICENSE";
}

export interface ChooseDoctorProcedureResponse {
  status: boolean;
  status_code: number;
  data: {
    registration_progress: string;
    message: string;
  };
}

// Doctor Information interfaces
export interface DoctorInfoRequest {
  // Backend-populated fields (not editable, removed from request):
  // - full_name
  // - medical_license_number, medical_license_issued_date, medical_license_expiry_date
  // - assossiation_specialization_order_number

  // Basic information
  sex: "male" | "female";
  email?: string; // optional
  date_of_birth: string; // YYYY-MM-DD - required

  // Professional info — نوع الطبيب decides which of the two ids is filled.
  // Send explicit null (not undefined) for the ones this type does not use: the
  // backend's exclude_unset skips absent fields, so undefined leaves stale values.
  doctor_type?: string; // specialist | practitioner | higher_diploma
  specialization_id?: string | null; // (UUID4) — specialists and higher-diploma holders
  practition_id?: string | null; // (UUID4) — general practitioners

  // Employment - employment_status determines which work fields are required
  employment_status?: string; // optional (EmploymentStatus enum)
  ministry?: string; // optional - required for employed statuses (Ministries enum)
  organization_id?: string; // optional - required for employed statuses (UUID4)
  hospital_id?: string; // optional (UUID4) - for HOSPITAL_EMPLOYEE
  health_center_id?: string; // optional (UUID4) - for HEALTH_CENTER_EMPLOYEE
  sector_id?: string; // optional (UUID4) - for SECTOR_EMPLOYEE

  career_progression?: string; // optional (CareerProgression enum)
  years_of_experience?: number; // optional

  // Education
  medical_degree?: string; // optional (MedicalDegree enum)
  graduated_from_id?: string | null; // (UUID4) university where graduated; null when "Other"
  graduated_from_other?: string | null; // free-text university name for degrees from abroad ("Other"); null otherwise
  graduation_year: number; // required

  // Work/Employment
  work_college_id?: string; // optional (UUID4) - for UNIVERSITY_EMPLOYEE

  // Clinic Information
  has_another_clinic: boolean; // required - has another clinic
  is_supervising_a_medical_center: boolean; // required - supervises medical center

  // Location
  governorate: string; // required (Governorate enum)
  city: string; // required
  neighborhood: number; // required
  street: number; // required
  house_number: number; // required

  // Settings
  language?: string; // optional (Language enum)
}

export interface DoctorInfoResponse {
  status: boolean;
  status_code: number;
  data: {
    registration_progress: string;
    message: string;
  };
}

// Degree and Specialties interfaces
export interface DegreeSpecialtiesRequest {
  university_id: string;
  specialization_id: string;
  graduation_date: string; // YYYY-MM-DD format
  degree_type: string;
  medical_career_path: string;
  degree_specialties: string;
  sub_specialty?: string;
  union_order_number?: string;
}

export interface DegreeSpecialtiesResponse {
  status: boolean;
  status_code: number;
  data: {
    registration_progress: string;
    message: string;
  };
}

// Clinic Information interfaces
export interface ClinicInfoRequest {
  name: string; // Clinic name [2, 255] characters
  governorate: string; // Governorate enum value
  area_id: string; // City area UUID
  neighborhood: number; // Neighborhood as integer
  street: number; // Street as integer
  house_number: number; // House number as integer
  nearest_landmark: string; // Nearest landmark [2, 255] characters
  latitude?: number | null; // Optional latitude (-90 to 90)
  longitude?: number | null; // Optional longitude (-180 to 180)
  clinic_work_hours: "morning" | "evening"; // ClinicWorkHours enum: "morning" or "evening"
  applicant_notes?: string | null; // Optional notes
  tax_number?: string | null;
  doctor_id?: string | null; // Optional doctor ID (auto-injected by backend)
}

export interface ClinicInfoResponse {
  status: boolean;
  status_code: number;
  data: {
    registration_progress: string;
    message: string;
  };
}

// Document Upload interfaces
export interface DocumentPresignedRequest {
  doc_types: string[];
}

export interface DocumentPresignedData {
  doc_type: string;
  upload_url: string;
  file_key: string;
  expires_at: string;
  fields?: Record<string, string>; // Optional form fields for S3 POST uploads
}

export interface DocumentPresignedResponse {
  status: boolean;
  status_code: number;
  data: {
    presigned_urls: DocumentPresignedData[];
  };
}

export interface DocumentInfo {
  id: string | null;
  doc_type: string;
  file_key: string;
  file_name: string;
  content_type: string;
}

export interface SaveDocumentsRequest {
  personal_documents: DocumentInfo[];
  clinic_documents: DocumentInfo[];
  next_step: boolean;
}

export interface SaveDocumentsResponse {
  status: boolean;
  status_code: number;
  data: {
    registration_progress: string;
    message: string;
  };
}

// Delete document interfaces
export interface GenerateDeleteUrlsRequest {
  document_ids: string[];
}

export interface DeleteUrlInfo {
  document_id: string;
  delete_url: string;
  s3_key: string;
  doc_type: string;
}

export interface GenerateDeleteUrlsResponse {
  status: boolean;
  status_code: number;
  message: string | null;
  data: {
    delete_urls: DeleteUrlInfo[];
    expires_in: number;
  } | null;
}

// Document delete result
export interface DocumentDeleteResult {
  document_id: string;
  success: boolean;
  status?: number;
}

// Agree Terms interfaces
export interface AgreeTermsRequest {
  agreed_to_terms: boolean;
}

export interface AgreeTermsResponse {
  status: boolean;
  status_code: number;
  data: {
    registration_progress: string;
    message: string;
  };
}

// Download presigned URL interfaces
export interface DocumentDownloadRequest {
  doc_types: string[];
}

export interface DocumentDownloadData {
  doc_type: string;
  download_url: string;
  expires_at: string;
}

export interface DocumentDownloadResponse {
  status: boolean;
  status_code: number;
  data: {
    presigned_urls: DocumentDownloadData[];
  };
}

// Universities and Specializations interfaces
export interface University {
  id: string;
  name: string;
  name_ar: string;
  code: string;
  country: string;
  city: string;
  university_type: string | null;
  accreditation_status: string | null;
  established_year: number;
  website: string | null;
  is_active: boolean;
  is_recognized: boolean;
  sort_order: number;
  created_at: string;
  updated_at: string;
}

export interface UniversitiesResponse {
  status: boolean;
  count: number;
  total: number;
  page_size: number;
  current_page: number;
  has_next: boolean;
  next: string | null;
  previous: string | null;
  data: University[];
  message: string;
  error: string | null;
}

export interface Specialization {
  id: string;
  name: string;
  name_ar: string;
  code: string;
  description: string | null;
  category: string;
  parent_id: string | null;
  is_active: boolean;
  sort_order: number;
  created_at: string;
  updated_at: string;
}

export interface SpecializationsResponse {
  status: boolean;
  count: number;
  total: number;
  page_size: number;
  current_page: number;
  has_next: boolean;
  next: string | null;
  previous: string | null;
  data: Specialization[];
  message: string;
  error: string | null;
}

export interface Practitioner {
  id: string;
  name: string;
  name_ar: string;
  code: string;
  description: string | null;
  category: string;
  parent_id: string | null;
  is_active: boolean;
  sort_order: number;
  created_at: string;
  updated_at: string;
}

export interface PractitionersResponse {
  status: boolean;
  count: number;
  total: number;
  page_size: number;
  current_page: number;
  has_next: boolean;
  next: string | null;
  previous: string | null;
  data: Practitioner[];
  message: string;
  error: string | null;
}

export interface College {
  id: string;
  name: string;
  name_ar: string;
  code: string;
  university_id: string;
  description: string | null;
  is_active: boolean;
  created_at: string;
  updated_at: string;
}

export interface CollegesResponse {
  status: boolean;
  count: number;
  total: number;
  page_size: number;
  current_page: number;
  has_next: boolean;
  next: string | null;
  previous: string | null;
  data: College[];
  message: string;
  error: string | null;
}

export interface Organization {
  id: string;
  name: string;
  name_ar: string;
  code: string;
  description: string | null;
  is_active: boolean;
  created_at: string;
  updated_at: string;
}

export interface OrganizationsResponse {
  status: boolean;
  count: number;
  total: number;
  page_size: number;
  current_page: number;
  has_next: boolean;
  next: string | null;
  previous: string | null;
  data: Organization[];
  message: string;
  error: string | null;
}

export interface Hospital {
  id: string;
  name: string;
  name_ar: string;
  code: string;
  organization_id: string;
  description: string | null;
  is_active: boolean;
  created_at: string;
  updated_at: string;
}

export interface HospitalsResponse {
  status: boolean;
  count: number;
  total: number;
  page_size: number;
  current_page: number;
  has_next: boolean;
  next: string | null;
  previous: string | null;
  data: Hospital[];
  message: string;
  error: string | null;
}

export interface Sector {
  id: string;
  name: string;
  name_ar: string | null;
  code: string | null;
  organization_id: string;
  governorate: string | null;
  city: string | null;
  address: string | null;
  phone_number: string | null;
  email: string | null;
  is_active: boolean;
  description: string | null;
  created_at: string | null;
  updated_at: string | null;
}

export interface SectorsResponse {
  status: boolean;
  count: number;
  total: number;
  page_size: number;
  current_page: number;
  has_next: boolean;
  next: string | null;
  previous: string | null;
  data: Sector[];
  message: string;
  error: string | null;
}

export interface HealthCenter {
  id: string;
  name: string;
  name_ar: string | null;
  code: string | null;
  sector_id: string;
  governorate: string;
  city: string | null;
  neighborhood: number | null;
  street: number | null;
  building_number: number | null;
  address: string | null;
  phone_number: string | null;
  emergency_phone: string | null;
  email: string | null;
  has_emergency: boolean;
  has_pharmacy: boolean;
  has_laboratory: boolean;
  is_active: boolean;
  license_number: string | null;
  license_issued_date: string | null;
  license_expiry_date: string | null;
  description: string | null;
  created_at: string | null;
  updated_at: string | null;
}

export interface HealthCentersResponse {
  status: boolean;
  count: number;
  total: number;
  page_size: number;
  current_page: number;
  has_next: boolean;
  next: string | null;
  previous: string | null;
  data: HealthCenter[];
  message: string;
  error: string | null;
}

export interface Area {
  id: string;
  name: string;
  name_ar: string;
  code: string;
  area_type: "country" | "governorate" | "city";
  governorate: string | null;
  parent_area_id: string | null;
  population: number | null;
  area_size_km2: number | null;
  coordinates: any | null;
  boundaries: any | null;
  is_active: boolean;
  postal_code: string | null;
  metadata: Record<string, any>;
  notes: string | null;
  total_clinics: number;
  active_clinics: number;
  assigned_doctors: number;
  created_at: string;
  updated_at: string;
}

export interface AreasResponse {
  status: boolean;
  count: number;
  total: number;
  page_size: number;
  current_page: number;
  has_next: boolean;
  next: string | null;
  previous: string | null;
  data: Area[];
  message: string;
  error: string | null;
}

// New Document Upload interfaces for the updated flow
export interface DocumentUploadUrlRequest {
  doc_type: string;
  category: "personal" | "clinic";
  file_count: number;
}

export interface DocumentUploadUrlData {
  file_index: number;
  upload_url: string;
  fields: Record<string, string>;
  file_key: string;
}

export interface DocumentUploadUrlResponse {
  success: boolean;
  message: string;
  data: {
    doc_type: string;
    category: "personal" | "clinic";
    upload_urls: DocumentUploadUrlData[];
    expires_in: number;
    max_file_size: number;
  };
}

export interface SaveDocumentRequest {
  doc_type: string;
  category: "personal" | "clinic";
  files: {
    file_key: string;
    file_name: string;
    content_type: string;
    size: string;
  }[];
}

export interface SaveDocumentResponse {
  status: boolean;
  status_code: number;
  data: {
    document_ids: string[];
    documents: Array<{
      document_id: string;
      file_name: string;
      download_url: string;
      expires_at: string;
      file_size: number;
      content_type: string;
    }>;
    doc_type: string;
    file_count: number;
    uploaded_at: string;
    message: string;
  };
}

export interface DeleteDocumentResponse {
  success: boolean;
  message: string;
  data: {
    deleted_document_id: string;
    doc_type: string;
    message: string;
  };
}

export interface ValidateDocumentsRequest {
  proceed_to_next: boolean;
}

export interface DocumentValidationInfo {
  doc_type: string;
  uploaded: boolean;
  required: boolean;
  file_count: number;
}

export interface ValidateDocumentsResponse {
  status: boolean;
  status_code: number;
  data: {
    personal_documents: Array<{
      doc_type: string;
      uploaded: boolean;
      required: boolean;
      file_count: number;
    }>;
    clinic_documents: Array<{
      doc_type: string;
      uploaded: boolean;
      required: boolean;
      file_count: number;
    }>;
    validation_summary: {
      all_required_uploaded: boolean;
      specialist_certificate_provided: boolean;
      missing_documents: string[];
      can_proceed: boolean;
    };
    registration_progress: string;
    message: string;
  };
}

export class RegistrationApiService {
  private baseURL =
    process.env.REACT_APP_API_BASE_URL || "http://localhost:8000";
  private authApi: AuthApiService;

  private get accessToken(): string | null {
    return this.authApi.getAccessToken();
  }

  constructor() {
    this.authApi = AuthApiService.getInstance();
  }

  // TEMPORARY: Method to update registration progress
  updateRegistrationProgress(step: string) {
    console.log(
      "🗃️ RegistrationApiService.updateRegistrationProgress called with:",
      step
    );
    console.log(
      "🗃️ Before update localStorage:",
      localStorage.getItem("registration_progress")
    );

    localStorage.setItem("registration_progress", step);

    // Sync stored doctorInfo structure if present
    const storedDoctorInfoStr = localStorage.getItem("doctorInfo");
    if (storedDoctorInfoStr) {
      try {
        const storedDoctorInfo = JSON.parse(storedDoctorInfoStr);
        if (storedDoctorInfo) {
          if (storedDoctorInfo.doctor_info) {
            storedDoctorInfo.doctor_info.registration_progress = step;
          }
          if (storedDoctorInfo.doctor_info) {
            storedDoctorInfo.doctor_info.registration_progress = step;
          }
          localStorage.setItem("doctorInfo", JSON.stringify(storedDoctorInfo));
          console.log("🗃️ doctorInfo registration_progress synced");
        }
      } catch (error) {
        console.warn(
          "⚠️ Failed to sync doctorInfo registration_progress:",
          error
        );
      }
    }

    // Sync standalone doctor_data cache if present
    const doctorDataStr = localStorage.getItem("doctor_data");
    if (doctorDataStr) {
      try {
        const doctorData = JSON.parse(doctorDataStr);
        if (doctorData) {
          doctorData.registration_progress = step;
          doctorData.registration_state = step;
          localStorage.setItem("doctor_data", JSON.stringify(doctorData));
          console.log("🗃️ doctor_data registration_progress synced");
        }
      } catch (error) {
        console.warn(
          "⚠️ Failed to sync doctor_data registration_progress:",
          error
        );
      }
    }

    console.log(
      "🗃️ After update localStorage:",
      localStorage.getItem("registration_progress")
    );
    console.log("✅ Registration progress updated to:", step);
  }


  async getMyInfo(): Promise<Result<any>> {
    return this.authApi.getMyInfo();
  }

  async refreshAccessToken(): Promise<Result<any>> {
    return this.authApi.refreshAccessToken();
  }

  isTokenValid(): boolean {
    return this.authApi.isTokenValid();
  }


  /**
   * Upload doctor document using multipart/form-data
   * POST /clinic/doctor/profile/documents/upload
   * @param file - The file to upload
   * @param documentType - One of: syndicate_id_face, syndicate_id_back, specialization_certificate
   */
  async uploadDoctorDocument(
    file: File,
    documentType: string
  ): Promise<Result<any>> {
    console.log(
      "📤 [uploadDoctorDocument] Starting upload:",
      documentType,
      "File:",
      file.name
    );

    try {
      // Create FormData and append the file with document type
      const formData = new FormData();
      formData.append("file", file, file.name);
      formData.append("document_type", documentType);

      console.log(
        "🌐 Making POST request to /clinic/doctor/profile/documents/upload"
      );
      console.log("📦 Uploading file:", file.name, "Size:", file.size, "bytes");
      console.log("📝 Document type:", documentType);

      const response = await this.authApi.fetchWithAuth(
        `${this.baseURL}/clinic/doctor/profile/documents/upload`,
        {
          method: "POST",
          body: formData,
        }
      );

      console.log("📥 Response status:", response.status, response.statusText);

      if (!response.ok) {
        const error = await response.json().catch(() => null);
        console.error("❌ Upload failed:", error);
        // Too large / unsupported type / empty / request no longer editable → localized message.
        return Result.failure(uploadErrorMessage(response.status, error, "Failed to upload document"));
      }

      const result = await response.json();
      console.log("✅ Upload successful:", result);

      // Trigger doctor info refresh
      triggerDoctorInfoRefresh();

      return Result.success(result.data || result);
    } catch (error) {
      console.error("❌ Exception during upload:", error);
      return Result.failure(
        error instanceof Error ? error.message : "Failed to upload document"
      );
    }
  }

  /**
   * Upload clinic attachment using XMLHttpRequest for better reliability
   * POST /clinic/doctor/registration/<request_id>/attachments/upload
   * @param file - The file to upload
   * @param documentType - One of: clinic_signboard_outer, clinic_signboard_inner, clinic_rooms, prescription_image
   * @param requestId - The doctor's request ID
   * @param onProgress - Optional progress callback
   */
  async uploadClinicAttachment(
    file: File,
    documentType: string,
    requestId: string,
    onProgress?: (percentage: number) => void
  ): Promise<Result<any>> {
    console.log(
      "📤 [uploadClinicAttachment] Starting upload:",
      documentType,
      "File:",
      file.name,
      "RequestID:",
      requestId
    );

    try {
      const url = `${this.baseURL}/clinic/doctor/registration/${requestId}/attachments/upload`;

      return new Promise((resolve) => {
        const xhr = new XMLHttpRequest();
        const formData = new FormData();

        formData.append("file", file, file.name);
        formData.append("document_type", documentType);

        // Track upload progress
        if (onProgress) {
          xhr.upload.addEventListener("progress", (e) => {
            if (e.lengthComputable) {
              const percentage = (e.loaded / e.total) * 100;
              onProgress(percentage);
            }
          });
        }

        // Handle successful response
        xhr.addEventListener("load", () => {
          if (xhr.status >= 200 && xhr.status < 300) {
            try {
              const result = JSON.parse(xhr.responseText);
              console.log(`✅ Upload successful for ${documentType}:`, result);

              // Note: No need to refresh doctor info here - clinic attachments don't affect doctor profile
              // Navigation is handled by the calling component after all uploads complete

              resolve(Result.success(result.data || result));
            } catch (error) {
              console.error("❌ Failed to parse response:", error);
              resolve(
                Result.failure("Server returned invalid response format")
              );
            }
          } else if (xhr.status === 401 || xhr.status === 444) {
            console.log("🔄 Token expired, attempting refresh...");
            // Try to refresh token
            this.refreshAccessToken().then((refreshResult) => {
              if (refreshResult.isSuccess) {
                // Retry the request
                this.uploadClinicAttachment(
                  file,
                  documentType,
                  requestId,
                  onProgress
                ).then(resolve);
              } else {
                resolve(Result.failure("Authentication failed"));
              }
            });
          } else {
            console.error(
              `❌ Upload failed with status ${xhr.status}:`,
              xhr.responseText
            );
            resolve(Result.failure(uploadErrorMessageFromText(xhr.status, xhr.responseText)));
          }
        });

        // Handle network errors
        xhr.addEventListener("error", () => {
          console.error("❌ Network error during upload");
          resolve(
            Result.failure(
              "Network error during upload. Please check your connection."
            )
          );
        });

        // Handle timeout
        xhr.addEventListener("timeout", () => {
          console.error("❌ Upload timeout");
          resolve(Result.failure("Upload timeout. Please try again."));
        });

        // Handle abort
        xhr.addEventListener("abort", () => {
          console.log("⚠️ Upload aborted");
          resolve(Result.failure("Upload cancelled"));
        });

        // Open connection and send
        xhr.open("POST", url);
        xhr.setRequestHeader("Authorization", `Bearer ${this.accessToken}`);
        xhr.timeout = 60000; // 60 second timeout

        console.log(
          `📤 Uploading ${documentType}: ${file.name} (${file.size} bytes)`
        );
        xhr.send(formData);
      });
    } catch (error) {
      console.error("❌ Exception during upload:", error);
      return Result.failure(
        error instanceof Error
          ? error.message
          : "Failed to upload clinic attachment"
      );
    }
  }

  // Choose Doctor Procedure API Method
  async chooseDoctorProcedure(
    procedure: ChooseDoctorProcedureRequest["doctor_procedure"]
  ): Promise<Result<ChooseDoctorProcedureResponse["data"]>> {

    // Map new enum values to backend-expected labels for backward compatibility
    const procedureMap: Record<string, string> = {
      OPEN_CLINIC: "Open Clinic",
      REGISTER_EXISTING_CLINIC: "Register Existing Clinic",
      TRANSFER_CLINIC: "Transfer Clinic",
      RELOCATE_CLINIC: "Relocate Clinic",
      CLOSE_CLINIC: "Close Clinic",
      RENEW_LICENSE: "Renew License",
    };

    const backendValue = (procedureMap as any)[procedure] || procedure;

    const requestBody: ChooseDoctorProcedureRequest = {
      doctor_procedure: backendValue as any,
    };

    try {
      const response = await this.authApi.fetchWithAuth(`${this.baseURL}/choose-doctor-procedure`, {
        method: "POST",
        body: JSON.stringify(requestBody),
      });

      if (!response.ok) {
        const error = await response.json();

        // Handle specific error codes
        switch (response.status) {
          case 400:
            return Result.failure(
              "Invalid registration progress. Complete previous steps first."
            );
          case 403:
            return Result.failure(
              "You don't have permission to access this feature."
            );
          default:
            return Result.failure(
              error.error_message ||
                error.detail ||
                error.message ||
                "Failed to choose doctor procedure"
            );
        }
      }

      const result: ChooseDoctorProcedureResponse = await response.json();

      if (!result.status) {
        return Result.failure(
          result.data?.message || "Failed to choose doctor procedure"
        );
      }

      return Result.success(result.data);
    } catch (error) {
      return Result.failure(
        error instanceof Error
          ? error.message
          : "Failed to choose doctor procedure"
      );
    }
  }

  // Doctor Information API Method
  async submitDoctorInfo(
    doctorInfo: DoctorInfoRequest
  ): Promise<Result<DoctorInfoResponse["data"]>> {

    try {
      const response = await this.authApi.fetchWithAuth(
        `${this.baseURL}/clinic/doctor/auth/doctor-profile`,
        {
          method: "PUT",
          body: JSON.stringify(doctorInfo),
        }
      );

      if (!response.ok) {
        let errorMessage = "Failed to submit doctor info";
        try {
          const error = await response.json();
          console.error("🚨 API Error Response:", error);
          errorMessage =
            error.error_message ||
            error.detail ||
            error.message ||
            `HTTP ${response.status}: ${response.statusText}`;
        } catch (parseError) {
          console.error("🚨 Failed to parse error response:", parseError);
          errorMessage = `HTTP ${response.status}: ${response.statusText}`;
        }

        return Result.failure(errorMessage);
      }

      const result: DoctorInfoResponse = await response.json();

      console.log("🎯 Doctor-profile API raw response:", result);
      console.log(
        "📊 Registration progress:",
        result.data?.registration_progress
      );
      console.log("💬 Response message:", result.data?.message);

      // Trigger refresh because registration progress advanced
      triggerDoctorInfoRefresh();

      return Result.success(result.data);
    } catch (error) {
      return Result.failure(
        error instanceof Error ? error.message : "Failed to submit doctor info"
      );
    }
  }

  // DEPRECATED: Degree and Specialties API Method
  // Degree and specialization fields are now part of updateDoctorProfile()
  async submitDegreeSpecialties(
    degreeInfo: DegreeSpecialtiesRequest
  ): Promise<Result<DegreeSpecialtiesResponse["data"]>> {

    try {
      const response = await this.authApi.fetchWithAuth(`${this.baseURL}/degree-and-specialties`, {
        method: "POST",
        body: JSON.stringify(degreeInfo),
      });

      if (!response.ok) {
        const error = await response.json();
        return Result.failure(
          error.error_message ||
            error.detail ||
            "Failed to submit degree and specialties"
        );
      }

      const result: DegreeSpecialtiesResponse = await response.json();

      // Trigger refresh because registration progress advanced
      triggerDoctorInfoRefresh();

      return Result.success(result.data);
    } catch (error) {
      return Result.failure(
        error instanceof Error
          ? error.message
          : "Failed to submit degree and specialties"
      );
    }
  }

  // Clinic Information API Method
  async submitClinicInfo(
    clinicInfo: ClinicInfoRequest
  ): Promise<Result<ClinicInfoResponse["data"]>> {

    try {
      const response = await this.authApi.fetchWithAuth(
        `${this.baseURL}/clinic/doctor/registration/`,
        {
          method: "POST",
          body: JSON.stringify(clinicInfo),
        }
      );

      if (!response.ok) {
        const error = await response.json();
        return Result.failure(
          error.error_message || error.detail || "Failed to submit clinic info"
        );
      }

      const result: ClinicInfoResponse = await response.json();

      // Trigger refresh because registration progress advanced
      triggerDoctorInfoRefresh();

      return Result.success(result.data);
    } catch (error) {
      return Result.failure(
        error instanceof Error ? error.message : "Failed to submit clinic info"
      );
    }
  }

  /**
   * Cancel a registration request (only valid while the request is in pre-submit / draft state).
   * POST /clinic/doctor/registration/{request_id}/cancel
   */
  async cancelRegistrationRequest(requestId: string): Promise<Result<any>> {
    if (isMockEnabled()) {
      console.log("[Mock] cancelRegistrationRequest", requestId);
      return Result.success({ message: "Mock cancel successful" });
    }

    try {
      const response = await this.authApi.fetchWithAuth(
        `${this.baseURL}/clinic/doctor/registration/${requestId}/cancel`,
        { method: "POST" }
      );

      if (!response.ok) {
        const error = await response.json();
        return Result.failure(
          error.detail || error.error_message || error.message || "Failed to cancel registration request"
        );
      }

      const result = await response.json();
      triggerDoctorInfoRefresh();
      return Result.success(result.data || result);
    } catch (error) {
      return Result.failure(
        error instanceof Error ? error.message : "Failed to cancel registration request"
      );
    }
  }

  // Update existing clinic registration (PUT)
  async updateClinicInfo(
    requestId: string,
    clinicInfo: ClinicInfoRequest
  ): Promise<Result<ClinicInfoResponse["data"]>> {
    try {
      const response = await this.authApi.fetchWithAuth(
        `${this.baseURL}/clinic/doctor/registration/${requestId}`,
        {
          method: "PUT",
          body: JSON.stringify(clinicInfo),
        }
      );

      if (!response.ok) {
        const error = await response.json();
        return Result.failure(
          error.error_message || error.detail || "Failed to update clinic info"
        );
      }

      const result: ClinicInfoResponse = await response.json();
      triggerDoctorInfoRefresh();
      return Result.success(result.data);
    } catch (error) {
      return Result.failure(
        error instanceof Error ? error.message : "Failed to update clinic info"
      );
    }
  }

  // Get clinic registration records for file upload
  async getClinicRegistrations(): Promise<Result<any[]>> {

    try {
      const response = await this.authApi.fetchWithAuth(
        `${this.baseURL}/clinic/doctor/registration/`,
        {
          method: "GET",
        }
      );

      if (!response.ok) {
        const error = await response.json();
        return Result.failure(
          error.error_message ||
            error.detail ||
            "Failed to get clinic registrations"
        );
      }

      const result = await response.json();
      return Result.success(result.data || []);
    } catch (error) {
      return Result.failure(
        error instanceof Error
          ? error.message
          : "Failed to get clinic registrations"
      );
    }
  }

  // Get a single registration with full attachment data
  async getRegistrationWithAttachments(requestId: string): Promise<Result<any>> {
    try {
      const response = await this.authApi.fetchWithAuth(
        `${this.baseURL}/clinic/doctor/registration/${requestId}/with-attachments`,
        { method: "GET" }
      );

      if (!response.ok) {
        const error = await response.json();
        return Result.failure(
          error.error_message || error.detail || "Failed to get registration details"
        );
      }

      const result = await response.json();
      return Result.success(result.data || result);
    } catch (error) {
      return Result.failure(
        error instanceof Error ? error.message : "Failed to get registration details"
      );
    }
  }

  // Upload file to S3 using the new attachment endpoint
  async uploadFileToAttachment(
    file: File,
    recordId: string,
    attachmentType: string = "syndicate_id"
  ): Promise<Result<any>> {

    try {
      // Create form data with only the file
      const formData = new FormData();
      formData.append("file", file);

      // Add required query parameters
      const queryParams = new URLSearchParams({
        record_id: recordId,
        record_model: "clinic_registration_request",
        attachment_type: attachmentType,
      });

      const response = await this.authApi.fetchWithAuth(
        `${this.baseURL}/attachment/upload-file?${queryParams}`,
        {
          method: "POST",
          body: formData,
        }
      );

      if (!response.ok) {
        const error = await response.json();
        return Result.failure(
          error.error_message || error.detail || "Failed to upload file"
        );
      }

      const result = await response.json();
      return Result.success(result.data);
    } catch (error) {
      return Result.failure(
        error instanceof Error ? error.message : "Failed to upload file"
      );
    }
  }

  // Document Upload Methods
  async generateDocumentUploadUrls(
    docTypes: string[]
  ): Promise<Result<DocumentPresignedData[]>> {

    try {
      const response = await this.authApi.fetchWithAuth(
        `${this.baseURL}/generate-document-presigned-urls`,
        {
          method: "POST",
          body: JSON.stringify({ doc_types: docTypes }),
        }
      );

      if (!response.ok) {
        const error = await response.json();
        return Result.failure(
          error.error_message ||
            error.detail ||
            "Failed to generate document upload URLs"
        );
      }

      const result: DocumentPresignedResponse = await response.json();
      return Result.success(result.data.presigned_urls);
    } catch (error) {
      return Result.failure(
        error instanceof Error
          ? error.message
          : "Failed to generate document upload URLs"
      );
    }
  }

  async saveDocuments(
    personalDocs: DocumentInfo[],
    clinicDocs: DocumentInfo[],
    nextStep: boolean = true
  ): Promise<Result<SaveDocumentsResponse["data"]>> {

    const requestBody: SaveDocumentsRequest = {
      personal_documents: personalDocs,
      clinic_documents: clinicDocs,
      next_step: nextStep,
    };

    try {
      const response = await this.authApi.fetchWithAuth(`${this.baseURL}/save-documents`, {
        method: "POST",
        body: JSON.stringify(requestBody),
      });

      if (!response.ok) {
        const error = await response.json();
        return Result.failure(
          error.error_message || error.detail || "Failed to save documents"
        );
      }

      const result: SaveDocumentsResponse = await response.json();

      if (!result.status) {
        return Result.failure(
          result.data?.message || "Failed to save documents"
        );
      }

      // Trigger refresh because documents uploaded might change progress
      triggerDoctorInfoRefresh();

      return Result.success(result.data);
    } catch (error) {
      return Result.failure(
        error instanceof Error ? error.message : "Failed to save documents"
      );
    }
  }

  /**
   * Generate presigned URLs for deleting documents
   */
  async generateDocumentDeleteUrls(
    documentIds: string[]
  ): Promise<Result<GenerateDeleteUrlsResponse["data"]>> {
    const requestBody: GenerateDeleteUrlsRequest = {
      document_ids: documentIds,
    };

    try {
      const response = await this.authApi.fetchWithAuth(
        `${this.baseURL}/generate-document-delete-urls`,
        {
          method: "POST",
          body: JSON.stringify(requestBody),
        }
      );

      if (!response.ok) {
        const errorData = await response.json().catch(() => null);
        return Result.failure(
          errorData?.detail ||
            `Failed to generate delete URLs: ${response.statusText}`
        );
      }

      const data: GenerateDeleteUrlsResponse = await response.json();
      return Result.success(data.data);
    } catch (error) {
      return Result.failure(
        "Network error occurred. Please check your connection and try again."
      );
    }
  }

  /**
   * Delete documents using the new two-step process
   */
  async deleteDocuments(
    documentIds: string[]
  ): Promise<Result<DocumentDeleteResult[]>> {
    try {
      // Step 1: Generate delete URLs
      const urlResult = await this.generateDocumentDeleteUrls(documentIds);

      if (!urlResult.isSuccess || !urlResult.data) {
        return Result.failure(
          urlResult.error || "Failed to generate delete URLs"
        );
      }

      // Step 2: Delete each document using the presigned URLs
      const deletePromises = urlResult.data.delete_urls.map(
        async (deleteInfo) => {
          try {
            const deleteResponse = await this.authApi.fetchWithAuth(deleteInfo.delete_url, {
              method: "DELETE",
            });

            return {
              document_id: deleteInfo.document_id,
              success: deleteResponse.ok,
              status: deleteResponse.status,
            };
          } catch (error) {
            return {
              document_id: deleteInfo.document_id,
              success: false,
            };
          }
        }
      );

      const results = await Promise.all(deletePromises);
      return Result.success(results);
    } catch (error) {
      return Result.failure(
        "Network error occurred. Please check your connection and try again."
      );
    }
  }

  /**
   * Agree to terms and conditions
   */
  async agreeToTerms(
    agreed: boolean,
    requestId: string
  ): Promise<Result<AgreeTermsResponse["data"]>> {

    try {
      console.log("📋 [agreeToTerms] Using request ID:", requestId);

      // POST to /clinic/doctor/registration/<req_id>/agree-to-terms
      const response = await this.authApi.fetchWithAuth(
        `${this.baseURL}/clinic/doctor/registration/${requestId}/agree-to-terms`,
        {
          method: "POST",
        }
      );

      if (!response.ok) {
        const error = await response.json();
        return Result.failure(
          error.error_message || error.detail || "Failed to agree to terms"
        );
      }

      const result: AgreeTermsResponse = await response.json();

      if (!result.status) {
        return Result.failure(
          result.data?.message || "Failed to agree to terms"
        );
      }

      // Trigger refresh because agreeing to terms might advance progress
      triggerDoctorInfoRefresh();

      return Result.success(result.data);
    } catch (error) {
      return Result.failure(
        error instanceof Error ? error.message : "Failed to agree to terms"
      );
    }
  }

  // TEMPORARY: Return mock data for document download URLs until backend is ready
  async generateDocumentDownloadUrls(
    docTypes: string[]
  ): Promise<Result<DocumentDownloadData[]>> {
    console.log(
      "🚫 generateDocumentDownloadUrls temporarily disabled - returning mock data"
    );
    console.log("📄 Requested doc types:", docTypes);

    // Return mock data structure to prevent UI errors
    const mockData: DocumentDownloadData[] = docTypes.map((docType) => ({
      doc_type: docType,
      download_url: "", // Empty URL since endpoint is not working
      expires_at: new Date(Date.now() + 3600000).toISOString(), // 1 hour from now
    }));

    return Result.success(mockData);
  }

  // New Document Upload Methods for the updated flow

  /**
   * Generate presigned URL for document upload
   */
  async generateDocumentUploadUrl(
    docType: string,
    category: "personal" | "clinic",
    fileCount: number
  ): Promise<Result<DocumentUploadUrlResponse["data"]>> {

    try {
      const response = await this.authApi.fetchWithAuth(
        `${this.baseURL}/generate-document-upload-url`,
        {
          method: "POST",
          body: JSON.stringify({
            doc_type: docType,
            category: category,
            file_count: fileCount,
          }),
        }
      );

      if (!response.ok) {
        const error = await response.json();
        return Result.failure(error.detail || "Failed to generate upload URL");
      }

      const result: DocumentUploadUrlResponse = await response.json();
      return Result.success(result.data);
    } catch (error) {
      return Result.failure(
        error instanceof Error ? error.message : "Failed to generate upload URL"
      );
    }
  }

  /**
   * Save document metadata after S3 upload
   */
  async saveDocument(
    request: SaveDocumentRequest
  ): Promise<Result<SaveDocumentResponse["data"]>> {

    try {
      const response = await this.authApi.fetchWithAuth(`${this.baseURL}/save-document`, {
        method: "POST",
        body: JSON.stringify(request),
      });

      if (!response.ok) {
        const error = await response.json();
        return Result.failure(error.detail || "Failed to save document");
      }

      const result: SaveDocumentResponse = await response.json();
      return Result.success(result.data);
    } catch (error) {
      return Result.failure(
        error instanceof Error ? error.message : "Failed to save document"
      );
    }
  }

  /**
   * Delete a document by ID
   */
  async deleteDocument(
    documentId: string
  ): Promise<Result<DeleteDocumentResponse["data"]>> {

    try {
      const response = await this.authApi.fetchWithAuth(
        `${this.baseURL}/delete-document/${documentId}`,
        {
          method: "DELETE",
        }
      );

      if (!response.ok) {
        const error = await response.json();
        return Result.failure(error.detail || "Failed to delete document");
      }

      const result: DeleteDocumentResponse = await response.json();
      return Result.success(result.data);
    } catch (error) {
      return Result.failure(
        error instanceof Error ? error.message : "Failed to delete document"
      );
    }
  }

  /**
   * Validate all documents and optionally proceed to next step
   */
  async validateDocuments(
    proceedToNext: boolean
  ): Promise<Result<ValidateDocumentsResponse["data"]>> {

    try {
      const response = await this.authApi.fetchWithAuth(`${this.baseURL}/validate-documents`, {
        method: "POST",
        body: JSON.stringify({
          proceed_to_next: proceedToNext,
        }),
      });

      if (!response.ok) {
        const error = await response.json();
        return Result.failure(error.detail || "Failed to validate documents");
      }

      const result: ValidateDocumentsResponse = await response.json();
      return Result.success(result.data);
    } catch (error) {
      return Result.failure(
        error instanceof Error ? error.message : "Failed to validate documents"
      );
    }
  }

  /**
   * Upload files directly to S3 using presigned URLs
   */
  async uploadFileToS3(
    file: File,
    uploadUrl: string,
    fields: Record<string, string>
  ): Promise<Result<void>> {
    try {
      const formData = new FormData();

      // Add all the fields from the presigned URL first
      Object.entries(fields).forEach(([key, value]) => {
        formData.append(key, value);
      });

      // Add Content-Type
      formData.append("Content-Type", file.type);

      // Add the file last (S3 requirement)
      formData.append("file", file);

      const response = await this.authApi.fetchWithAuth(uploadUrl, {
        method: "POST",
        body: formData,
      });

      if (!response.ok) {
        return Result.failure(`S3 upload failed: ${response.statusText}`);
      }

      // Return a dummy value (true) to indicate success since the caller only checks isSuccess
      return Result.success(true as unknown as void);
    } catch (error) {
      return Result.failure(
        error instanceof Error ? error.message : "Failed to upload file to S3"
      );
    }
  }

  /**
   * Validate navigation to a target step in the three-section registration flow
   */
  async validateNavigation(
    targetStep: string
  ): Promise<Result<NavigationValidationResponse["data"]>> {
    try {
      const token = this.accessToken;
      if (!token) {
        return Result.failure("No authentication token available");
      }

      const response = await this.authApi.fetchWithAuth(`${this.baseURL}/validate-navigation`, {
        method: "POST",
        body: JSON.stringify({
          target_step: targetStep,
        }),
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => null);
        return Result.failure(
          errorData?.detail ||
            `HTTP ${response.status}: Failed to validate navigation`
        );
      }

      const result: NavigationValidationResponse = await response.json();

      if (!result.status) {
        return Result.failure(
          result.data?.error_message || "Navigation validation failed"
        );
      }

      return Result.success(result.data);
    } catch (error) {
      return Result.failure(
        error instanceof Error
          ? error.message
          : "Network error during navigation validation"
      );
    }
  }

  /**
   * Fetch universities list
   */
  async getUniversities(
    page: number = 1,
    pageSize: number = 500,
    includeDeleted: boolean = false
  ): Promise<Result<University[]>> {
    if (isMockEnabled()) {
      return Result.success(MOCK_UNIVERSITIES as University[]);
    }


    try {
      const url = `${this.baseURL}/clinic/doctor/universities/?page=${page}&page_size=${pageSize}&include_deleted=${includeDeleted}`;

      console.log("🔄 Fetching universities:", url);

      const response = await this.authApi.fetchWithAuth(url, {
        method: "GET",
      });

      if (!response.ok) {
        const error = await response.json();
        console.log("❌ Universities fetch error:", error);
        return Result.failure(
          error.detail || error.message || "Failed to fetch universities"
        );
      }

      const result: UniversitiesResponse = await response.json();
      console.log(
        "✅ Universities fetched successfully:",
        result.data.length,
        "universities"
      );

      if (!result.status) {
        return Result.failure(result.message || "Failed to fetch universities");
      }

      return Result.success(result.data);
    } catch (error) {
      console.log("💥 Universities fetch exception:", error);
      return Result.failure(
        error instanceof Error ? error.message : "Failed to fetch universities"
      );
    }
  }

  /**
   * Fetch specializations list
   */
  async getSpecializations(
    page: number = 1,
    pageSize: number = 500,
    includeDeleted: boolean = false
  ): Promise<Result<Specialization[]>> {
    if (isMockEnabled()) {
      return Result.success(MOCK_SPECIALIZATIONS as Specialization[]);
    }


    try {
      const url = `${this.baseURL}/clinic/doctor/specializations/?page=${page}&page_size=${pageSize}&include_deleted=${includeDeleted}`;

      console.log("🔄 Fetching specializations:", url);

      const response = await this.authApi.fetchWithAuth(url, {
        method: "GET",
      });

      if (!response.ok) {
        const error = await response.json();
        console.log("❌ Specializations fetch error:", error);
        return Result.failure(
          error.detail || error.message || "Failed to fetch specializations"
        );
      }

      const result: SpecializationsResponse = await response.json();
      console.log(
        "✅ Specializations fetched successfully:",
        result.data.length,
        "specializations"
      );

      if (!result.status) {
        return Result.failure(
          result.message || "Failed to fetch specializations"
        );
      }

      return Result.success(result.data);
    } catch (error) {
      console.log("💥 Specializations fetch exception:", error);
      return Result.failure(
        error instanceof Error
          ? error.message
          : "Failed to fetch specializations"
      );
    }
  }

  /**
   * Fetch practitioners list
   */
  async getPractitioners(
    page: number = 1,
    pageSize: number = 500,
    includeDeleted: boolean = false
  ): Promise<Result<Practitioner[]>> {
    if (isMockEnabled()) {
      return Result.success(MOCK_PRACTITIONERS as Practitioner[]);
    }


    try {
      const url = `${this.baseURL}/clinic/doctor/practitions/?page=${page}&page_size=${pageSize}&include_deleted=${includeDeleted}`;

      console.log("🔄 Fetching practitioners:", url);

      const response = await this.authApi.fetchWithAuth(url, {
        method: "GET",
      });

      if (!response.ok) {
        const error = await response.json();
        console.log("❌ Practitioners fetch error:", error);
        return Result.failure(
          error.detail || error.message || "Failed to fetch practitioners"
        );
      }

      const result: PractitionersResponse = await response.json();
      console.log(
        "✅ Practitioners fetched successfully:",
        result.data.length,
        "practitioners"
      );

      if (!result.status) {
        return Result.failure(
          result.message || "Failed to fetch practitioners"
        );
      }

      return Result.success(result.data);
    } catch (error) {
      console.log("💥 Practitioners fetch exception:", error);
      return Result.failure(
        error instanceof Error ? error.message : "Failed to fetch practitioners"
      );
    }
  }

  /**
   * Fetch colleges list (optionally filtered by university)
   */
  async getColleges(
    universityId?: string,
    page: number = 1,
    pageSize: number = 500,
    includeDeleted: boolean = false
  ): Promise<Result<College[]>> {

    try {
      let url = `${this.baseURL}/clinic/doctor/colleges/?page=${page}&page_size=${pageSize}&include_deleted=${includeDeleted}`;

      // Add university filter if provided
      if (universityId) {
        url += `&university_id=${universityId}`;
      }

      console.log("🔄 Fetching colleges:", url);

      const response = await this.authApi.fetchWithAuth(url, {
        method: "GET",
      });

      if (!response.ok) {
        const error = await response.json();
        console.log("❌ Colleges fetch error:", error);
        return Result.failure(
          error.detail || error.message || "Failed to fetch colleges"
        );
      }

      const result: CollegesResponse = await response.json();
      console.log(
        "✅ Colleges fetched successfully:",
        result.data.length,
        "colleges"
      );

      if (!result.status) {
        return Result.failure(result.message || "Failed to fetch colleges");
      }

      return Result.success(result.data);
    } catch (error) {
      console.log("💥 Colleges fetch exception:", error);
      return Result.failure(
        error instanceof Error ? error.message : "Failed to fetch colleges"
      );
    }
  }

  /**
   * Update doctor profile information
   */
  async updateDoctorProfile(
    doctorInfo: DoctorInfoRequest
  ): Promise<Result<any>> {

    try {
      const url = `${this.baseURL}/clinic/doctor/profile/me`;

      console.log("🔄 Updating doctor profile:", url);
      console.log("📤 Payload:", doctorInfo);

      const response = await this.authApi.fetchWithAuth(url, {
        method: "PUT",
        body: JSON.stringify(doctorInfo),
      });

      if (!response.ok) {
        const error = await response.json();
        console.log("❌ Doctor profile update error:", error);
        return Result.failure(
          error.detail || error.message || "Failed to update doctor profile"
        );
      }

      const result = await response.json();
      console.log("✅ Doctor profile updated successfully:", result);

      // Trigger doctor info refresh after successful update
      triggerDoctorInfoRefresh();

      return Result.success(result);
    } catch (error) {
      console.log("💥 Doctor profile update exception:", error);
      return Result.failure(
        error instanceof Error
          ? error.message
          : "Failed to update doctor profile"
      );
    }
  }

  /**
   * Fetch organizations list
   */
  async getOrganizations(
    page: number = 1,
    pageSize: number = 500,
    includeDeleted: boolean = false,
    ministry?: string
  ): Promise<Result<Organization[]>> {
    if (isMockEnabled()) {
      return Result.success(MOCK_ORGANIZATIONS as Organization[]);
    }


    try {
      let url = `${this.baseURL}/clinic/doctor/organizations/?page=${page}&page_size=${pageSize}&include_deleted=${includeDeleted}`;

      // Add ministry filter if provided
      if (ministry) {
        url += `&ministry=${encodeURIComponent(ministry)}`;
      }

      console.log("🔄 Fetching organizations:", url);

      const response = await this.authApi.fetchWithAuth(url, {
        method: "GET",
      });

      if (!response.ok) {
        const error = await response.json();
        console.log("❌ Organizations fetch error:", error);
        return Result.failure(
          error.detail || error.message || "Failed to fetch organizations"
        );
      }

      const result: OrganizationsResponse = await response.json();
      console.log(
        "✅ Organizations fetched successfully:",
        result.data.length,
        "organizations"
      );

      if (!result.status) {
        return Result.failure(
          result.message || "Failed to fetch organizations"
        );
      }

      return Result.success(result.data);
    } catch (error) {
      console.log("💥 Organizations fetch exception:", error);
      return Result.failure(
        error instanceof Error ? error.message : "Failed to fetch organizations"
      );
    }
  }

  /**
   * Fetch hospitals list (optionally filtered by organization)
   */
  async getHospitals(
    organizationId?: string,
    page: number = 1,
    pageSize: number = 500,
    includeDeleted: boolean = false
  ): Promise<Result<Hospital[]>> {
    if (isMockEnabled()) {
      return Result.success(MOCK_HOSPITALS as Hospital[]);
    }


    try {
      let url = `${this.baseURL}/clinic/doctor/hospitals/?page=${page}&page_size=${pageSize}&include_deleted=${includeDeleted}`;

      // Add organization filter if provided
      if (organizationId) {
        url += `&organization_id=${organizationId}`;
      }

      console.log("🔄 Fetching hospitals:", url);

      const response = await this.authApi.fetchWithAuth(url, {
        method: "GET",
      });

      if (!response.ok) {
        const error = await response.json();
        console.log("❌ Hospitals fetch error:", error);
        return Result.failure(
          error.detail || error.message || "Failed to fetch hospitals"
        );
      }

      const result: HospitalsResponse = await response.json();
      console.log(
        "✅ Hospitals fetched successfully:",
        result.data.length,
        "hospitals"
      );

      if (!result.status) {
        return Result.failure(result.message || "Failed to fetch hospitals");
      }

      return Result.success(result.data);
    } catch (error) {
      console.log("💥 Hospitals fetch exception:", error);
      return Result.failure(
        error instanceof Error ? error.message : "Failed to fetch hospitals"
      );
    }
  }

  /**
   * Fetch sectors list (optionally filtered by organization)
   */
  async getSectors(
    organizationId?: string,
    page: number = 1,
    pageSize: number = 500,
    includeDeleted: boolean = false
  ): Promise<Result<Sector[]>> {

    try {
      let url = `${this.baseURL}/clinic/doctor/sectors/?page=${page}&page_size=${pageSize}&include_deleted=${includeDeleted}`;

      if (organizationId) {
        url += `&organization_id=${organizationId}`;
      }

      console.log("🔄 Fetching sectors:", url);

      const response = await this.authApi.fetchWithAuth(url, {
        method: "GET",
      });

      if (!response.ok) {
        const error = await response.json();
        console.log("❌ Sectors fetch error:", error);
        return Result.failure(
          error.detail || error.message || "Failed to fetch sectors"
        );
      }

      const result: SectorsResponse = await response.json();
      console.log(
        "✅ Sectors fetched successfully:",
        result.data.length,
        "sectors"
      );

      if (!result.status) {
        return Result.failure(result.message || "Failed to fetch sectors");
      }

      return Result.success(result.data);
    } catch (error) {
      console.log("💥 Sectors fetch exception:", error);
      return Result.failure(
        error instanceof Error ? error.message : "Failed to fetch sectors"
      );
    }
  }

  /**
   * Fetch health centers list (optionally filtered by sector)
   */
  async getHealthCenters(
    sectorId?: string,
    page: number = 1,
    pageSize: number = 500,
    includeDeleted: boolean = false
  ): Promise<Result<HealthCenter[]>> {

    try {
      let url = `${this.baseURL}/clinic/doctor/health-centers/?page=${page}&page_size=${pageSize}&include_deleted=${includeDeleted}`;

      if (sectorId) {
        url += `&sector_id=${sectorId}`;
      }

      console.log("🔄 Fetching health centers:", url);

      const response = await this.authApi.fetchWithAuth(url, {
        method: "GET",
      });

      if (!response.ok) {
        const error = await response.json();
        console.log("❌ Health centers fetch error:", error);
        return Result.failure(
          error.detail || error.message || "Failed to fetch health centers"
        );
      }

      const result: HealthCentersResponse = await response.json();
      console.log(
        "✅ Health centers fetched successfully:",
        result.data.length,
        "health centers"
      );

      if (!result.status) {
        return Result.failure(result.message || "Failed to fetch health centers");
      }

      return Result.success(result.data);
    } catch (error) {
      console.log("💥 Health centers fetch exception:", error);
      return Result.failure(
        error instanceof Error ? error.message : "Failed to fetch health centers"
      );
    }
  }

  /**
   * Fetch areas list
   */
  async getAreas(
    page: number = 1,
    pageSize: number = 500,
    includeDeleted: boolean = false,
    governorate?: string
  ): Promise<Result<Area[]>> {

    try {
      let url = `${this.baseURL}/base/doctor/areas/?page=${page}&page_size=${pageSize}&include_deleted=${includeDeleted}&area_type=city`;
      if (governorate) {
        url += `&governorate=${encodeURIComponent(governorate)}`;
      }

      console.log("🔄 Fetching areas:", url);

      const response = await this.authApi.fetchWithAuth(url, {
        method: "GET",
      });

      if (!response.ok) {
        const error = await response.json();
        console.log("❌ Areas fetch error:", error);
        return Result.failure(
          error.detail || error.message || "Failed to fetch areas"
        );
      }

      const result: AreasResponse = await response.json();
      console.log(
        "✅ Areas fetched successfully:",
        result.data.length,
        "areas"
      );

      if (!result.status) {
        return Result.failure(result.message || "Failed to fetch areas");
      }

      return Result.success(result.data);
    } catch (error) {
      console.log("💥 Areas fetch exception:", error);
      return Result.failure(
        error instanceof Error ? error.message : "Failed to fetch areas"
      );
    }
  }

  /**
   * Initiate payment for registration
   */
  async initiatePayment(
    returnUrl: string,
    cancelUrl?: string,
    overrideRequestId?: string,
    overrideRequestType?: string
  ): Promise<Result<any>> {

    try {
      let requestId: string;
      let requestType: string;

      if (overrideRequestId && overrideRequestType) {
        requestId = overrideRequestId;
        requestType = overrideRequestType;
      } else {
        return Result.failure("No active request found. Missing request ID and type.");
      }

      console.log("💳 [initiatePayment] Request ID:", requestId);
      console.log("💳 [initiatePayment] Request Type:", requestType);

      const payload: any = {
        request_id: requestId,
        request_type: requestType,
        return_url: returnUrl,
      };

      // Add cancel_url if provided
      if (cancelUrl) {
        payload.cancel_url = cancelUrl;
      }

      const response = await this.authApi.fetchWithAuth(
        `${this.baseURL}/clinic/doctor/payment/initiate`,
        {
          method: "POST",
          body: JSON.stringify(payload),
        }
      );

      if (!response.ok) {
        const error = await response.json();
        console.error("💳 [initiatePayment] API Error Response:", error);
        return Result.failure(
          error?.error?.message || error.detail || error.message || "فشل في بدء عملية الدفع",
          { status: response.status, message_code: error?.error?.message_code }
        );
      }

      const result = await response.json();
      console.log("💳 [initiatePayment] Raw API Response:", result);
      console.log("💳 [initiatePayment] Response data:", result.data);
      console.log(
        "💳 [initiatePayment] Data keys:",
        result.data ? Object.keys(result.data) : "No data"
      );
      return Result.success(result.data);
    } catch (error) {
      return Result.failure(
        error instanceof Error
          ? error.message
          : "فشل في بدء عملية الدفع. يرجى المحاولة مرة أخرى."
      );
    }
  }

  /**
   * Check payment status
   */
  /**
   * The fee (IQD) the syndicate charges per request type, straight from the
   * backend's PAYMENT_*_FEE settings.
   *
   * The amounts used to be constants in this app and drifted from the server the
   * moment a price changed (the closure fee was raised on the API while the app
   * still advertised the old one). Public endpoint — no auth needed.
   */
  async getPaymentFees(): Promise<Result<PaymentFees>> {
    try {
      const response = await fetch(`${this.baseURL}/clinic/doctor/payment/fees`, {
        method: "GET",
        headers: { "Content-Type": "application/json" },
      });

      if (!response.ok) {
        return Result.failure("Failed to load payment fees");
      }

      const result = await response.json();
      return Result.success(result.data as PaymentFees);
    } catch (error) {
      return Result.failure(
        error instanceof Error ? error.message : "Failed to load payment fees"
      );
    }
  }

  /**
   * Ask the server to re-check a payment with Qi Card (POST /verify/{reference}). The plain GET only
   * reads what the server stored, which changes when Qi's webhook arrives; this settles a lost or late
   * webhook without waiting for the reconciliation job. Callers read the payment afterwards.
   */
  async verifyPayment(paymentReference: string): Promise<Result<any>> {
    try {
      const response = await this.authApi.fetchWithAuth(
        `${this.baseURL}/clinic/doctor/payment/verify/${encodeURIComponent(paymentReference)}`,
        { method: "POST" }
      );
      if (!response.ok) {
        return Result.failure("verify failed", { status: response.status });
      }
      const result = await response.json();
      return Result.success(result.data);
    } catch (error) {
      return Result.failure(error instanceof Error ? error.message : "verify failed");
    }
  }

  async checkPaymentStatus(paymentId: string): Promise<Result<any>> {

    try {
      const response = await this.authApi.fetchWithAuth(
        `${this.baseURL}/clinic/doctor/payment/${paymentId}`,
        {
          method: "GET",
        }
      );

      if (!response.ok) {
        const error = await response.json();
        // 404 = not this doctor's payment (or gone): the screen stops polling on it.
        return Result.failure(error?.error?.message || error.detail || "فشل في التحقق من حالة الدفع", {
          status: response.status,
          message_code: error?.error?.message_code,
        });
      }

      const result = await response.json();
      return Result.success(result.data);
    } catch (error) {
      return Result.failure(
        error instanceof Error
          ? error.message
          : "تعذر التحقق من حالة الدفع. يرجى التحقق من اتصال الإنترنت والمحاولة مرة أخرى."
      );
    }
  }

  /**
   * STAGING-ONLY: skip payment and submit the request. The backend hard-gates this
   * (returns 404 in production), so it can never succeed in production.
   */
  async bypassPayment(
    requestId: string,
    requestType: "registration" | "closure"
  ): Promise<Result<any>> {
    try {
      const response = await this.authApi.fetchWithAuth(
        `${this.baseURL}/clinic/doctor/payment/bypass`,
        {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ request_id: requestId, request_type: requestType }),
        }
      );
      if (!response.ok) {
        const error = await response.json();
        return Result.failure(error?.error?.message || error?.detail || "Payment bypass not available");
      }
      const result = await response.json();
      return Result.success(result.data);
    } catch (error) {
      return Result.failure(
        error instanceof Error ? error.message : "Payment bypass failed"
      );
    }
  }

  /**
   * Cancel payment
   */
  async cancelPayment(paymentId: string): Promise<Result<any>> {

    try {
      const response = await this.authApi.fetchWithAuth(
        `${this.baseURL}/clinic/doctor/payment/cancel`,
        {
          method: "POST",
          body: JSON.stringify({
            payment_id: paymentId,
          }),
        }
      );

      if (!response.ok) {
        const error = await response.json();
        return Result.failure(error?.error?.message || error.detail || "فشل في إلغاء الدفع", {
          status: response.status,
          message_code: error?.error?.message_code,
        });
      }

      const result = await response.json();
      return Result.success(result.data);
    } catch (error) {
      return Result.failure(
        error instanceof Error
          ? error.message
          : "تعذر إلغاء الدفع. يرجى التحقق من اتصال الإنترنت والمحاولة مرة أخرى."
      );
    }
  }
}
